import React, { useState, useEffect, useRef } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  getDocs,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, firestore, storage } from '../views/firebaseConfig';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import profileManageIcon from '../assets/android/ui/p4r_business_profile_manage.svg';
import photoIcon from '../assets/android/ui/p4r_business_photo_icon.svg';
import tablesIcon from '../assets/android/ui/p4r_business_tables.svg';
import hoursIcon from '../assets/android/ui/p4r_business_hours.svg';
import offerAlcohol from '../assets/android/ui/p4r_business_offer_alcohol.svg';
import offerSmoke from '../assets/android/ui/p4r_business_offer_smoke.svg';
import offerFood from '../assets/android/ui/p4r_business_offer_food.svg';
import offer8 from '../assets/android/ui/p4r_business_offer_8ball.svg';
import offer9 from '../assets/android/ui/p4r_business_offer_9ball.svg';
import offerCarom from '../assets/android/ui/p4r_business_offer_carom.svg';
import offerRussian from '../assets/android/ui/p4r_business_offer_russian.svg';
import offerSnooker from '../assets/android/ui/p4r_business_offer_snooker.svg';
import './BusinessEditor.css';

// Default hours template
const defaultBusinessHours = {
  Monday:    { open: '', close: '', isClosed: false },
  Tuesday:   { open: '', close: '', isClosed: false },
  Wednesday: { open: '', close: '', isClosed: false },
  Thursday:  { open: '', close: '', isClosed: false },
  Friday:    { open: '', close: '', isClosed: false },
  Saturday:  { open: '', close: '', isClosed: false },
  Sunday:    { open: '', close: '', isClosed: false },
};


const offeringMeta={hasAlcohol:['Alcohol',offerAlcohol],hasSmokingLounge:['Smoking lounge',offerSmoke],hasFood:['Food',offerFood],has8Ball:['8 Ball',offer8],has9Ball:['9 Ball',offer9],hasCarambola:['Carom',offerCarom],hasRussianPyramid:['Russian Pyramid',offerRussian],hasSnooker:['Snooker',offerSnooker]};

const normalizeLocation = (loc = {}) => ({
  businessName:'',businessBrand:'',businessBio:'',address:'',phone:'',businessEmail:'',poolTables:'',profileImageUrl:'',secondImageUrl:'',businessStatus:'inactive',businessType:'Pool Venue',isMain:false,subviewEnabled:false,defaultSubview:'Events',
  businessOfferingFields:{hasAlcohol:false,hasSmokingLounge:false,hasFood:false,has8Ball:false,has9Ball:false,hasCarambola:false,hasRussianPyramid:false,hasSnooker:false,...(loc.businessOfferingFields||{})},
  businessHoursEnabled:false,sameHoursForAllDays:false,businessHours:{...defaultBusinessHours,...(loc.businessHours||{})},...loc
});

const BusinessEditor = ({ onClose = () => {}, show = true }) => {
  const [hasBusinessProfile, setHasBusinessProfile] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [formData, setFormData] = useState({
    businessName:           '',
    businessBrand:          '',
    businessBio:            '',
    address:                '',
    phone:                  '',
    businessEmail:          '',
    poolTables:             '',
    profileImageUrl:        '',
    secondImageUrl:         '',
    businessStatus:         'inactive',
    businessType:           'Pool Venue',
    isMain:                 false,
    subviewEnabled:         false,
    defaultSubview:         'Events',
    businessOfferingFields: {
      hasAlcohol: false,
      hasSmokingLounge: false,
      hasFood: false,
      has8Ball: false,
      has9Ball: false,
      hasCarambola: false,
      hasRussianPyramid: false,
      hasSnooker: false,
    },
    businessHoursEnabled:   false,
    sameHoursForAllDays:    false,
    businessHours:          defaultBusinessHours
  });

  const [selectedProfileImage, setSelectedProfileImage] = useState(null);
  const [selectedSecondImage, setSelectedSecondImage] = useState(null);
  const [previewProfile, setPreviewProfile] = useState('');
  const [previewSecond, setPreviewSecond] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const profileInputRef = useRef();
  const secondInputRef = useRef();

  // 1️⃣ READ existing profiles—this never writes anything
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(firestore, 'businessprofiles'),
      where('businessId', '==', user.uid),
      where('searchEnabled', '==', true),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snap => {
      const locs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLocations(locs);
      setHasBusinessProfile(locs.length > 0);

      // If nothing selected yet, pick the first existing profile
      if (!selectedLocation && locs.length > 0) {
        setSelectedLocation(locs[0]);
        setFormData(normalizeLocation(locs[0]));
      }
    });

    return () => unsubscribe();
  }, [selectedLocation]);

  // 2️⃣ Update image previews whenever user picks files or changes selection
  useEffect(() => {
    setPreviewProfile(
      selectedProfileImage
        ? URL.createObjectURL(selectedProfileImage)
        : selectedLocation?.profileImageUrl || ''
    );
    setPreviewSecond(
      selectedSecondImage
        ? URL.createObjectURL(selectedSecondImage)
        : selectedLocation?.secondImageUrl || ''
    );
  }, [selectedProfileImage, selectedSecondImage, selectedLocation]);

  // 3️⃣ Generic form change handler (just updates local state)
  const handleChange = e => {
    const { name, value, type, checked } = e.target;

    if (name in (formData.businessOfferingFields || {})) {
      setFormData(fd => ({
        ...fd,
        businessOfferingFields: {
          ...fd.businessOfferingFields,
          [name]: checked
        }
      }));
    } else if (
      ['businessHoursEnabled','sameHoursForAllDays','isMain','subviewEnabled'].includes(name)
    ) {
      setFormData(fd => ({ ...fd, [name]: checked }));
    } else {
      setFormData(fd => ({ ...fd, [name]: value }));
    }
  };

  // 4️⃣ Business hours handler
  const handleBusinessHoursChange = (day, field, value) => {
    setFormData(fd => ({
      ...fd,
      businessHours: {
        ...fd.businessHours,
        [day]: { ...fd.businessHours[day], [field]: value }
      }
    }));
  };

  // 5️⃣ Handle file inputs (no writes here—just keep the file object in state)
  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (type === 'profile') {
      setSelectedProfileImage(file);
      profileInputRef.current.value = '';
    } else {
      setSelectedSecondImage(file);
      secondInputRef.current.value = '';
    }
  };

  // 6️⃣ Upload an image to Storage and return its download URL
  const uploadImage = async (file, path) => {
    const fileRef = ref(
      storage,
      `businessProfiles/${auth.currentUser.uid}/${path}`
    );
    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
  };

  // 7️⃣ WRITE to Firestore: runs ONLY when “Save Changes” is clicked
  const saveProfile = async () => {
    console.log('▶︎ saveProfile() invoked—no writes happen until this line.');
    const required = [
      'businessBrand',
      'businessBio',
      'address',
      'phone',
      'businessEmail',
      'businessType'
    ];
    const allFilled = required.every(k => formData[k]?.trim());
    const imagesReady =
      (selectedProfileImage || formData.profileImageUrl) &&
      (selectedSecondImage || formData.secondImageUrl);
    if (!allFilled || !imagesReady) {
      setError(
        'Please fill all required fields and upload both images (map icon + business image).'
      );
      return;
    }
    if (formData.businessHoursEnabled) {
      const valid = formData.sameHoursForAllDays
        ? !!(
            formData.businessHours.Monday.open &&
            formData.businessHours.Monday.close
          )
        : Object.values(formData.businessHours).some(d => !d.isClosed);
      if (!valid) {
        setError('Please ensure at least one open day in your business hours.');
        return;
      }
    }

    setIsSaving(true);
    setError('');

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user.');

      let docRef;
      if (selectedLocation?.id) {
        // Updating an existing document—no new doc is created here
        docRef = doc(firestore, 'businessprofiles', selectedLocation.id);
      } else {
        // Creating a brand-new document only now, when Save is clicked
        const mainQuery = query(
          collection(firestore, 'businessprofiles'),
          where('businessId', '==', user.uid)
        );
        const snap = await getDocs(mainQuery);
        if (snap.empty) {
          // If no profiles exist, use user.uid as the doc ID
          docRef = doc(firestore, 'businessprofiles', user.uid);
        } else {
          // Otherwise generate a fresh Firestore ID
          docRef = doc(collection(firestore, 'businessprofiles'));
        }
      }

      // Build the object that will be written
      const updated = {
        businessName:           formData.businessName,
        businessBrand:          formData.businessBrand,
        businessBio:            formData.businessBio,
        address:                formData.address,
        phone:                  formData.phone,
        businessEmail:          formData.businessEmail,
        poolTables:             formData.poolTables,
        businessType:           formData.businessType,
        businessStatus:         formData.businessStatus,
        isMain:                 formData.isMain,
        subviewEnabled:         formData.subviewEnabled,
        defaultSubview:         formData.defaultSubview,
        businessOfferingFields: formData.businessOfferingFields,
        businessHoursEnabled:   formData.businessHoursEnabled,
        sameHoursForAllDays:    formData.sameHoursForAllDays,
        businessHours:          formData.businessHours,
        searchEnabled:          true,
        businessId:             user.uid,
        locationId:             docRef.id,
        timestamp:              serverTimestamp(),
        profileImageUrl:        formData.profileImageUrl,
        secondImageUrl:         formData.secondImageUrl
      };

      // Upload images if the user selected new ones
      if (selectedProfileImage) {
        updated.profileImageUrl = await uploadImage(
          selectedProfileImage,
          `${docRef.id}/profile.png`
        );
      }
      if (selectedSecondImage) {
        updated.secondImageUrl = await uploadImage(
          selectedSecondImage,
          `${docRef.id}/second.png`
        );
      }

      // ▶︎ THIS LINE actually writes to Firestore. Until you click “Save Changes,” nothing is written.
      await setDoc(docRef, updated, { merge: true });

      // If “isMain” was toggled on, turn off any existing main location on other docs
      if (updated.isMain) {
        const allSnap = await getDocs(
          query(
            collection(firestore, 'businessprofiles'),
            where('businessId', '==', user.uid)
          )
        );
        for (const ds of allSnap.docs) {
          if (ds.id !== docRef.id && ds.data().isMain) {
            await updateDoc(
              doc(firestore, 'businessprofiles', ds.id),
              { isMain: false }
            );
          }
        }
      }

      setSelectedLocation({ id: docRef.id, ...updated });
      onClose(); // Close modal after save
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // 8️⃣ WRITE to Firestore: Delete only when you click the “Delete” button
  const deleteLocation = async () => {
    if (!selectedLocation) return;
    if (
      !window.confirm(
        'Are you sure you want to delete this location? This action cannot be undone.'
      )
    )
      return;
    try {
      // ▶︎ This is the only delete call
      await deleteDoc(doc(firestore, 'businessprofiles', selectedLocation.id));
      // Reset local state so no branch is selected
      setSelectedLocation(null);
      setFormData({
        businessName:           '',
        businessBrand:          '',
        businessBio:            '',
        address:                '',
        phone:                  '',
        businessEmail:          '',
        poolTables:             '',
        profileImageUrl:        '',
        secondImageUrl:         '',
        businessStatus:         'inactive',
        businessType:           'Pool Venue',
        isMain:                 false,
        subviewEnabled:         false,
        defaultSubview:         'Events',
        businessOfferingFields: {
          hasAlcohol: false,
          hasSmokingLounge: false,
          hasFood: false,
          has8Ball: false,
          has9Ball: false,
          hasCarambola: false,
          hasRussianPyramid: false,
          hasSnooker: false,
        },
        businessHoursEnabled:   false,
        sameHoursForAllDays:    false,
        businessHours:          defaultBusinessHours
      });
      setPreviewProfile('');
      setPreviewSecond('');
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        centered
        scrollable
        backdrop="static"
        dialogClassName="p4r-business-editor-dialog"
        contentClassName="p4r-business-editor-modal"
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          className="p4r-business-editor-header"
        >
          <Modal.Title className="flex-grow-1 text-center">
            <img src={profileManageIcon} alt="" className="p4r-business-editor-title-icon" /> Manage business location
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          className="p4r-business-editor-body"
          style={{ maxHeight: '75vh', overflowY: 'auto' }}
        >
          {error && <Alert variant="danger">{error}</Alert>}

          <Alert variant="info" className="mb-4 text-center">
            📌 Fill out your business details below. Fields marked with{' '}
            <span className="text-danger">*</span> are required.
            Once verified, you’ll be able to display your location on the map
            and use engagement tools.
          </Alert>

          <Form>
            {/* ── Select Branch ── */}
            <Form.Group className="mb-3">
              <Form.Label>Select Branch</Form.Label>
              <Form.Text className="text-muted d-block">
                Pick an existing branch to edit, or leave blank to create a new
                one.
              </Form.Text>
              <Form.Select
                className="bg-dark text-light"
                value={selectedLocation?.id || ''}
                onChange={e => {
                  const loc = locations.find(l => l.id === e.target.value);
                  if (loc) {
                    setSelectedLocation(loc);
                    setFormData(normalizeLocation(loc));
                  } else {
                    setSelectedLocation(null);
                    // Reset to empty form
                    setFormData({
                      businessName:           '',
                      businessBrand:          '',
                      businessBio:            '',
                      address:                '',
                      phone:                  '',
                      businessEmail:          '',
                      poolTables:             '',
                      profileImageUrl:        '',
                      secondImageUrl:         '',
                      businessStatus:         'inactive',
                      businessType:           'Pool Venue',
                      isMain:                 false,
                      subviewEnabled:         false,
                      defaultSubview:         'Events',
                      businessOfferingFields: {
                        hasAlcohol: false,
                        hasSmokingLounge: false,
                        hasFood: false,
                        has8Ball: false,
                        has9Ball: false,
                        hasCarambola: false,
                        hasRussianPyramid: false,
                        hasSnooker: false,
                      },
                      businessHoursEnabled:   false,
                      sameHoursForAllDays:    false,
                      businessHours:          defaultBusinessHours
                    });
                  }
                }}
              >
                <option value="">Select a location</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.address || loc.businessBrand}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* ── Legal Name (internal only) ── */}
            <Form.Group className="mb-3">
              <Form.Label>Legal Name (Not Visible by Users)</Form.Label>
              <Form.Text className="text-muted d-block">
                This won’t be publicly displayed; it’s for internal use.
              </Form.Text>
              <Form.Control
                className="bg-dark text-light"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                maxLength={30}
                placeholder="Optional, up to 30 chars"
              />
            </Form.Group>

            {/* ── Business Brand ── */}
            <Form.Group className="mb-3">
              <Form.Label>
                Business Brand <span className="text-danger">*</span>
              </Form.Label>
              <Form.Text className="text-muted d-block">
                The name customers will see on the map.
              </Form.Text>
              <Form.Control
                className="bg-dark text-light"
                name="businessBrand"
                value={formData.businessBrand}
                onChange={handleChange}
              />
            </Form.Group>

            {/* ── Address, Phone, Email ── */}
            <Form.Group className="mb-3">
              <Form.Label>
                Address <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                className="bg-dark text-light"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Phone <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                className="bg-dark text-light"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                className="bg-dark text-light"
                name="businessEmail"
                value={formData.businessEmail}
                onChange={handleChange}
              />
            </Form.Group>

            {/* ── Map Icon & Business Image ── */}
            <Form.Group className="mb-3">
              <Form.Label><img src={photoIcon} alt="" className="p4r-inline-asset"/> Map Icon <span className="text-danger">*</span></Form.Label><Form.Text className="text-muted d-block">Ideal size: 1024 × 1024 px.</Form.Text>
              <Form.Control
                type="file"
                className="bg-dark text-light"
                ref={profileInputRef}
                onChange={e => handleImageChange(e, 'profile')}
              />
              {previewProfile && (
                <img
                  src={previewProfile}
                  alt="Map Icon"
                  className="rounded-circle mt-2"
                  style={{ width: 80, height: 80 }}
                />
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label><img src={photoIcon} alt="" className="p4r-inline-asset"/> Business Image <span className="text-danger">*</span></Form.Label><Form.Text className="text-muted d-block">Ideal size: 1600 × 900 px or larger.</Form.Text>
              <Form.Control
                type="file"
                className="bg-dark text-light"
                ref={secondInputRef}
                onChange={e => handleImageChange(e, 'second')}
              />
              {previewSecond && (
                <img
                  src={previewSecond}
                  alt="Business"
                  className="rounded mt-2"
                  style={{ width: '100%', maxHeight: 200, objectFit: 'cover' }}
                />
              )}
            </Form.Group>

            {/* ── Bio & Type ── */}
            <Form.Group className="mb-3">
              <Form.Label>
                Business Bio <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="bg-dark text-light"
                name="businessBio"
                value={formData.businessBio}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Business Type <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="businessType"
                className="bg-dark text-light"
                value={formData.businessType}
                onChange={handleChange}
              >
                <option value="Pool Venue">Pool Venue</option>
                <option value="Retailer">Retailer</option>
                <option value="Service">Service</option>
                <option value="Manufacturer">Manufacturer</option>
              </Form.Select>
            </Form.Group>

            {/* ── Main/Subview Toggles ── */}
            <Form.Check
              type="switch"
              className="text-light mb-3"
              name="isMain"
              label="Main Location"
              checked={formData.isMain}
              onChange={handleChange}
            />
            <Form.Check
              type="switch"
              className="text-light mb-3"
              name="subviewEnabled"
              label="Enable Subview (e.g. Stuff 4 Sale)"
              checked={formData.subviewEnabled}
              onChange={handleChange}
            />
            {formData.subviewEnabled && (
              <Form.Group className="mb-3">
                <Form.Label>Default Subview</Form.Label>
                <Form.Select
                  name="defaultSubview"
                  className="bg-dark text-light"
                  value={formData.defaultSubview}
                  onChange={handleChange}
                >
                  <option value="Events">Events</option>
                  <option value="4 Sale">4 Sale</option>
                </Form.Select>
              </Form.Group>
            )}

            {/* ── Pool Tables ── */}
            <Form.Group className="mb-3">
              <Form.Label><img src={tablesIcon} alt="" className="p4r-inline-asset"/> Pool Tables (Optional)</Form.Label>
              <Form.Control
                type="number"
                name="poolTables"
                className="bg-dark text-light"
                value={formData.poolTables}
                onChange={handleChange}
              />
            </Form.Group>

            {/* ── Offerings & Game Types ── */}
            <hr className="text-secondary" />
            <h5 className="p4r-editor-section-title">Offerings &amp; Game Types</h5>
            <div className="row">
              {Object.entries(formData.businessOfferingFields).map(([key, val]) => (
                <div className="col-md-4 mb-2" key={key}>
                  <Form.Check
                    type="switch"
                    className="text-light"
                    label={<span className="p4r-offering-label"><img src={offeringMeta[key]?.[1]} alt=""/>{offeringMeta[key]?.[0] || key}</span>}
                    name={key}
                    checked={val}
                    onChange={handleChange}
                  />
                </div>
              ))}
            </div>

            {/* ── Business Hours ── */}
            <hr className="text-secondary" />
            <div className="p4r-editor-section-title"><img src={hoursIcon} alt="" className="p4r-inline-asset"/> Business Hours</div><Form.Check
              type="switch"
              className="text-light mb-3"
              name="businessHoursEnabled"
              label="Enable Business Hours"
              checked={formData.businessHoursEnabled}
              onChange={handleChange}
            />
            {formData.businessHoursEnabled && (
              <>
                <Form.Check
                  type="switch"
                  className="text-light mb-3"
                  name="sameHoursForAllDays"
                  label="Same Hours For All Days"
                  checked={formData.sameHoursForAllDays}
                  onChange={handleChange}
                />
                {Object.entries(formData.businessHours).map(([day, info]) => (
                  <div key={day} className="mb-2">
                    <strong>{day}</strong>
                    <Form.Check
                      type="switch"
                      className="text-light"
                      label="Closed"
                      checked={info.isClosed}
                      onChange={e =>
                        handleBusinessHoursChange(day, 'isClosed', e.target.checked)
                      }
                    />
                    {!info.isClosed && (
                      <div className="d-flex gap-3 mt-2 align-items-center">
                        <div>
                          <Form.Label className="mb-0">Open</Form.Label>
                          <Form.Control
                            type="time"
                            value={info.open}
                            onChange={e =>
                              handleBusinessHoursChange(day, 'open', e.target.value)
                            }
                            className="bg-dark text-light"
                          />
                        </div>
                        <div>
                          <Form.Label className="mb-0">Close</Form.Label>
                          <Form.Control
                            type="time"
                            value={info.close}
                            onChange={e =>
                              handleBusinessHoursChange(day, 'close', e.target.value)
                            }
                            className="bg-dark text-light"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer className="p4r-business-editor-footer">
          <Button
            variant="outline-light"
            onClick={deleteLocation}
            disabled={!selectedLocation}
          >
            Delete
          </Button>
          <Button variant="primary" onClick={saveProfile} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BusinessEditor;
